import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface VersionDeletionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  versionType: 'estimate' | 'change_order';
  versionNumber: number;
  versionStatus: string;
  isLoading?: boolean;
}

const VersionDeletionDialog: React.FC<VersionDeletionDialogProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
  versionType,
  versionNumber,
  versionStatus,
  isLoading = false
}) => {
  const isApproved = versionStatus === 'approved';
  const actionText = isApproved ? 'cancel' : 'delete';
  const warningText = isApproved 
    ? 'This will mark the version as cancelled and cannot be undone.'
    : 'This will permanently delete the version and cannot be undone.';

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <AlertDialogTitle>
              {isApproved ? 'Cancel' : 'Delete'} {versionType === 'change_order' ? 'Change Order' : 'Version'}?
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>
              Are you sure you want to {actionText}{' '}
              <strong>
                {versionType === 'change_order' ? 'Change Order' : 'Version'} v{versionNumber}
              </strong>
              ?
            </p>
            <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
              <p className="text-sm text-destructive font-medium">
                ⚠️ Warning: {warningText}
              </p>
            </div>
            {!isApproved && (
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm text-muted-foreground">
                  All data associated with this version, including line items and pricing information, will be permanently lost.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? 'Processing...' : `${isApproved ? 'Cancel' : 'Delete'} Version`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default VersionDeletionDialog;